/**
 * Client for the tenant-scoped obligation & binding API (LEGAL-5/6) —
 * /:customer/legal/* on tri-server.
 *
 * Read law (obligations, provenance, coverage) / write bindings only.
 */

import { configuration } from "./Configuration";
import { ApplicationError, UnauthorizedError } from "./general/Errors";
import { authenticatedFetch } from "./JWTAuthService";

const processResponse = async (response: Response) => {
  if (response.ok) {
    return response.json();
  }

  if (response.status === 401) {
    throw new UnauthorizedError();
  }

  let errorMessage = `${response.status} ${response.statusText}`;

  try {
    const errorData = await response.json();
    errorMessage = errorData.message || errorData.error || errorMessage;
  } catch {
    // response body not JSON — keep default message
  }

  throw new ApplicationError(errorMessage);
};

export interface BindingScope {
  anchor: { type: "Portfolio" | "Site"; siteId?: string };
  regulatedObjects: "ALL" | string[];
}

export const useLegalBindingAPI = (customer: string) => {
  const baseUrl = `${configuration.serverUrl}/${customer}/legal`;

  const get = async (path: string, params?: Record<string, any>) => {
    const query = params
      ? `?${new URLSearchParams(
          Object.entries(params)
            .filter(([, value]) => value !== undefined && value !== null && value !== "")
            .map(([key, value]) => [key, String(value)]),
        ).toString()}`
      : "";
    const response = await authenticatedFetch(`${baseUrl}${path}${query}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });

    return processResponse(response);
  };

  const send = async (method: "POST" | "PUT", path: string, body?: any) => {
    const response = await authenticatedFetch(`${baseUrl}${path}`, {
      method,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      headers: { "Content-Type": "application/json" },
    });

    return processResponse(response);
  };

  return {
    // Read law (tenant-scoped projection, read-only)
    getObligations: (includeAll: boolean = false) => get("/obligations", { includeAll }),
    getObligationProvenance: (obligationKey: string) =>
      get("/obligation-provenance", { key: obligationKey }),
    getRegulatedObjects: () => get("/regulated-objects"),
    getCoverage: () => get("/coverage"),
    getSites: () => get("/sites"),

    // Write bindings
    getBindings: () => get("/bindings"),
    previewBinding: (
      scope: BindingScope,
      delegatedTo: any | null,
      conformanceClass: string,
      options: { includeUnconfirmed?: boolean } = {}
    ) => send("POST", "/bindings/preview", { scope, delegatedTo, conformanceClass, ...options }),
    /** Subscribe; `activate: false` subscribes without creating checklists yet. */
    createBinding: (
      scope: BindingScope,
      delegatedTo: any | null,
      conformanceClass: string,
      options: { activate?: boolean; includeUnconfirmed?: boolean } = {}
    ) => send("POST", "/bindings", { scope, delegatedTo, conformanceClass, ...options }),
    activateBinding: (bindingId: string) => send("POST", `/bindings/${bindingId}/activate`),
    stopBinding: (bindingId: string, reason?: string) => send("POST", `/bindings/${bindingId}/stop`, { reason }),

    // LEGAL-13 cost
    getCostConfiguration: () => get("/cost-configuration"),
    saveCostConfiguration: (config: { currency?: string; roleCosts: any[]; durationDefaults: any[] }) =>
      send("PUT", "/cost-configuration", config),
    costEstimate: (scope: BindingScope, conformanceClass: string) =>
      send("POST", "/cost-estimate", { scope, conformanceClass }),
    costCompare: (scope: BindingScope, options: { includeUnconfirmed?: boolean } = {}) =>
      send("POST", "/cost-estimate?compare=true", { scope, includeUnconfirmed: options.includeUnconfirmed === true }),
    getCostBreakdown: () => get("/cost-breakdown"),
    /** Measured checklist durations (median per duty, own and all tenants). */
    getChecklistDurations: () => get("/checklist-durations"),
    /** The checklist an obligation would produce (master, definition or the duty itself). */
    getChecklistPreview: (obligationKey: string, bindingId?: string) =>
      get("/obligations/checklist-preview", bindingId ? { key: obligationKey, bindingId } : { key: obligationKey }),
    /** The checklist of a merged appointment — several duties, one sample asset (tri-server previewMergedChecklist). */
    getMergedChecklistPreview: (obligationKeys: string[], assetId: string, bindingId?: string) =>
      get("/obligations/checklist-preview", {
        keys: obligationKeys.join(","),
        assetId,
        ...(bindingId ? { bindingId } : {}),
      }),
    /** What activated subscriptions entail — open, upcoming per role (quarter, 1/2 years), cost. */
    getDashboard: (bindingId?: string) => get("/dashboard", bindingId ? { bindingId } : {}),
    suspendBinding: (bindingId: string, reason?: string) =>
      send("POST", `/bindings/${bindingId}/suspend`, { reason }),
    resumeBinding: (bindingId: string) => send("POST", `/bindings/${bindingId}/resume`),
    endBinding: (bindingId: string, reason?: string) =>
      send("POST", `/bindings/${bindingId}/end`, { reason }),
    setBindingDelegation: (bindingId: string, delegatedTo: any | null) =>
      send("PUT", `/bindings/${bindingId}/delegation`, { delegatedTo }),
    getBindingArtifacts: (bindingId: string) => get(`/bindings/${bindingId}/artifacts`),

    // Obligation exclusions (negative mask over a subscription's obligation set)
    queryBindingObligations: (
      bindingId: string,
      filter: { din276?: string[]; regulationId?: string[]; text?: string; obligationType?: string[] },
    ) => send("POST", `/bindings/${bindingId}/obligations/query`, filter),
    getBindingExclusions: (bindingId: string) => get(`/bindings/${bindingId}/exclusions`),
    excludeObligations: (bindingId: string, obligationKeys: string[], comment?: string) =>
      send("POST", `/bindings/${bindingId}/exclusions`, { obligationKeys, comment }),
    revokeExclusions: (bindingId: string, obligationKeys: string[]) =>
      send("POST", `/bindings/${bindingId}/exclusions/revoke`, { obligationKeys }),

    // Asset legal keys + lifecycle
    getAssetObligations: (assetId: string) => get(`/assets/${assetId}/obligations`),
    resolveAssets: () => send("POST", "/assets/resolve"),
    // VDMA 24186 — capture a manual reading (a field/task action, never a rule edit).
    captureReading: (
      assetId: string,
      reading: { attributeName: string; value: number | string; unit?: string },
    ) => send("POST", `/assets/${assetId}/readings`, reading),
    setAssetState: (assetId: string, state: string, reason?: string) =>
      send("POST", `/assets/${assetId}/state`, { state, reason }),
    // License attestation (BYOL) — scoped licensed norms + append-only attest/revoke.
    getLicenseAttestations: (subscriptionId: string) =>
      get("/license-attestations", { subscriptionId }),
    setLicenseAttestation: (attestation: {
      subscriptionId: string;
      normId: string;
      eliId?: string;
      status: "attested" | "revoked";
      comment?: string;
    }) => send("POST", "/license-attestations", attestation),
    // VDMA A3 — operator-default maintenance intervals (bulk gewerk/asset-type + per-asset override).
    getMaintenanceIntervals: () => get("/maintenance-interval"),
    setMaintenanceInterval: (scope: {
      level?: "gewerk" | "assetType" | "asset";
      gewerk?: string;
      regulatedObjectCode?: string;
      assetId?: string;
      taskType?: string;
      months: number;
      confirm?: boolean;
    }) => send("PUT", "/maintenance-interval", scope),
  };
};
